import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Volume2, VolumeX } from 'lucide-react';
import { loadGoogleMapsApi, isGoogleMapsConfigured } from '../../utils/googleMapsLoader';
import { distanceMeters, type LatLng } from '../../utils/locationSmoothing';
import { fetchInAppRoute } from '../../utils/orsRouting';
import { geocodeInAbeokuta, looksMisplacedDestination } from '../../utils/geocodeDestination';
import { getNavMapStyle, GO7_COLORS, GO7_ROUTE_COLORS } from '../../utils/go7MapTheme';
import {
  createCameraState,
  getNavigationMapPadding,
  tickFollowCamera,
  type CameraState,
} from '../../utils/mapCamera';
import { go7MotorcycleMarkerIcon } from '../../utils/go7MapMarkers';
import { MapTilerNavMap } from './MapTilerNavMap';
import {
  buildCompassGuidance,
  buildTurnGuidance,
  extractRouteSteps,
  findActiveStepIndex,
  type NavStep,
  type TurnGuidance,
} from '../../utils/navigationSteps';

export interface NavMapLocation {
  lat: number;
  lng: number;
  address?: string;
  heading?: number;
}

export interface NavRouteStats {
  distanceMeters: number;
  durationMinutes: number;
  distanceLabel: string;
  etaLabel: string;
}

interface Go7StyleNavMapProps {
  pickupLocation?: NavMapLocation;
  deliveryLocation?: NavMapLocation;
  currentLocation?: NavMapLocation;
  /** Where to navigate — pickup or delivery */
  navTarget?: NavMapLocation;
  destinationLabel?: string;
  riderHeading?: number;
  riderSpeedMps?: number;
  accuracyM?: number | null;
  isMoving?: boolean;
  onRouteStatsUpdate?: (stats: NavRouteStats) => void;
}

/** Within this of destination = arrived — stop turn-by-turn / heading thrash */
const ARRIVED_RADIUS_M = 45;
const ROUTE_REFRESH_MS = 90_000;
/** Keep cost low — not every GPS tick */
const ROUTE_REFRESH_DISTANCE_METERS = 400;
const ACCURACY_MIN_M = 12;
const ACCURACY_MAX_M = 80;
const MARKER_ICON_MS = 180;

/**
 * Production in-app Google Maps navigation:
 * Directions + red route, RAF follow camera (bottom-biased), heading rotation,
 * dynamic zoom, accuracy circle, turn-by-turn with street names.
 */
export function Go7StyleNavMap({
  pickupLocation,
  deliveryLocation,
  currentLocation,
  navTarget,
  destinationLabel,
  riderHeading = 0,
  riderSpeedMps = 0,
  accuracyM = null,
  isMoving = false,
  onRouteStatsUpdate,
}: Go7StyleNavMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const directionsRendererRef = useRef<any>(null);
  const fallbackPolylineRef = useRef<any>(null);
  const markersRef = useRef<Record<string, any>>({});
  const accuracyCircleRef = useRef<any>(null);
  const trafficRef = useRef<any>(null);
  const lastRouteOriginRef = useRef<LatLng | null>(null);
  const cameraStateRef = useRef<CameraState | null>(null);
  const lastCameraTsRef = useRef(0);
  const lastIconTsRef = useRef(0);
  const cameraRafRef = useRef(0);

  // Live refs — GPS writes here; RAF reads (no panTo on every React tick)
  const livePosRef = useRef<LatLng | null>(null);
  const liveHeadingRef = useRef(0);
  const liveSpeedRef = useRef(0);
  const liveAccuracyRef = useRef<number | null>(null);
  const liveMovingRef = useRef(false);
  const liveArrivedRef = useRef(false);
  const liveDistToTurnRef = useRef<number | null>(null);

  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [useBackupMap, setUseBackupMap] = useState(false);
  const [steps, setSteps] = useState<NavStep[]>([]);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [etaText, setEtaText] = useState<string | null>(null);
  const [distanceText, setDistanceText] = useState<string | null>(null);
  const [resolvedTarget, setResolvedTarget] = useState<NavMapLocation | undefined>(navTarget);
  const [arrived, setArrived] = useState(false);
  const [hasRouteLine, setHasRouteLine] = useState(false);
  const lastSpokenStepRef = useRef(-1);
  const wasArrivedRef = useRef(false);

  const activeStep = steps[activeStepIndex] || null;
  const nextStep = steps[activeStepIndex + 1] || null;

  // Keep live refs in sync from props (no camera calls here)
  useEffect(() => {
    if (currentLocation?.lat) {
      livePosRef.current = { lat: currentLocation.lat, lng: currentLocation.lng };
    }
    if (riderHeading != null && Number.isFinite(riderHeading) && riderHeading >= 0) {
      liveHeadingRef.current = riderHeading;
    }
    liveSpeedRef.current = riderSpeedMps || 0;
    liveAccuracyRef.current = accuracyM;
    liveMovingRef.current = Boolean(isMoving);
  }, [
    currentLocation?.lat,
    currentLocation?.lng,
    riderHeading,
    riderSpeedMps,
    accuracyM,
    isMoving,
  ]);

  useEffect(() => {
    liveArrivedRef.current = arrived;
  }, [arrived]);

  // Resolve bad geocodes
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!navTarget?.lat && !navTarget?.address && !destinationLabel) {
        setResolvedTarget(undefined);
        return;
      }
      const label = navTarget?.address || destinationLabel || '';
      if (navTarget && !looksMisplacedDestination(navTarget, label)) {
        setResolvedTarget(navTarget);
        return;
      }
      if (!label) {
        setResolvedTarget(navTarget);
        return;
      }
      const fixed = await geocodeInAbeokuta(label);
      if (cancelled) return;
      if (fixed) {
        setResolvedTarget({ ...(navTarget || fixed), lat: fixed.lat, lng: fixed.lng, address: label });
      } else {
        setResolvedTarget(navTarget);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [navTarget?.lat, navTarget?.lng, navTarget?.address, destinationLabel]);

  // Init Google Map — on any failure (missing key, auth failure, Map class
  // unavailable) fall back to the MapTiler navigation map so the rider always
  // gets a follow camera instead of a dead screen.
  useEffect(() => {
    if (!containerRef.current) return;
    if (!isGoogleMapsConfigured()) {
      setUseBackupMap(true);
      setLoadError(true);
      return;
    }

    let cancelled = false;
    (window as any).gm_authFailure = () => {
      if (!cancelled) {
        setUseBackupMap(true);
        setLoadError(true);
      }
    };

    loadGoogleMapsApi()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const google = (window as any).google;
        if (!google?.maps?.Map) {
          setUseBackupMap(true);
          setLoadError(true);
          return;
        }

        const center =
          currentLocation || resolvedTarget || pickupLocation || deliveryLocation || {
            lat: 7.1575,
            lng: 3.347,
          };

        const map = new google.maps.Map(containerRef.current, {
          center,
          zoom: 17,
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
          // Avoid tilt/heading/padding in constructor — breaks follow on many Android WebViews
          backgroundColor: GO7_COLORS.darkGray,
          styles: getNavMapStyle(),
        });

        mapRef.current = map;
        cameraStateRef.current = createCameraState(center);

        try {
          map.setOptions({ padding: getNavigationMapPadding() });
        } catch {
          /* older Maps builds ignore padding */
        }

        directionsRendererRef.current = new google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          preserveViewport: true,
          polylineOptions: {
            strokeColor: GO7_ROUTE_COLORS.progress,
            strokeWeight: 8,
            strokeOpacity: 0.95,
          },
        });

        try {
          trafficRef.current = new google.maps.TrafficLayer();
          trafficRef.current.setMap(map);
        } catch {
          /* optional */
        }

        setMapReady(true);
        setLoadError(false);
        google.maps.event.trigger(map, 'resize');
      })
      .catch(() => {
        if (!cancelled) {
          setUseBackupMap(true);
          setLoadError(true);
        }
      });

    return () => {
      cancelled = true;
      if (cameraRafRef.current) cancelAnimationFrame(cameraRafRef.current);
      Object.values(markersRef.current).forEach((m) => m.setMap?.(null));
      markersRef.current = {};
      accuracyCircleRef.current?.setMap?.(null);
      accuracyCircleRef.current = null;
      directionsRendererRef.current?.setMap?.(null);
      fallbackPolylineRef.current?.setMap?.(null);
      trafficRef.current?.setMap?.(null);
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once
  }, []);

  // Re-apply padding on resize / orientation (iPhone + Android)
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      try {
        map.setOptions({ padding: getNavigationMapPadding() });
      } catch {
        /* ignore */
      }
    };
    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
    };
  }, [mapReady]);

  const clearFallbackLine = () => {
    if (fallbackPolylineRef.current) {
      fallbackPolylineRef.current.setMap(null);
      fallbackPolylineRef.current = null;
    }
  };

  const applyFallbackPath = (path: LatLng[]) => {
    const map = mapRef.current;
    const google = (window as any).google;
    if (!map || !google?.maps || path.length < 2) return;
    clearFallbackLine();
    fallbackPolylineRef.current = new google.maps.Polyline({
      path,
      strokeColor: GO7_ROUTE_COLORS.progress,
      strokeWeight: 8,
      strokeOpacity: 0.95,
      map,
    });
  };

  const fetchRoute = useCallback(
    (from: LatLng, to: LatLng) => {
      const google = (window as any).google;
      const map = mapRef.current;
      if (!map || !from?.lat || !to?.lat) return;

      if (distanceMeters(from, to) < ARRIVED_RADIUS_M) {
        setArrived(true);
        setSteps([]);
        setHasRouteLine(false);
        setEtaText('Arrived');
        setDistanceText('0 m');
        onRouteStatsUpdate?.({
          distanceMeters: 0,
          durationMinutes: 0,
          distanceLabel: '0 m',
          etaLabel: 'Arrived',
        });
        return;
      }

      if (liveAccuracyRef.current != null && liveAccuracyRef.current > 90) {
        return;
      }

      setArrived(false);

      // Always start Mapbox/ORS route immediately so riders see a line even if Google Directions is blocked
      void fetchInAppRoute(from, to, { force: true }).then((route) => {
        if (!route.path?.length) return;
        // Don't overwrite if Google Directions already drew a better route
        if (directionsRendererRef.current?.getDirections?.()) return;
        applyFallbackPath(route.path);
        setHasRouteLine(true);
        setEtaText(route.etaLabel);
        setDistanceText(route.distanceLabel);
        lastRouteOriginRef.current = { lat: from.lat, lng: from.lng };
        onRouteStatsUpdate?.({
          distanceMeters: route.distanceMeters,
          durationMinutes: route.durationMinutes,
          distanceLabel: route.distanceLabel,
          etaLabel: route.etaLabel,
        });
      });

      if (!google?.maps?.DirectionsService) return;

      const service = new google.maps.DirectionsService();
      service.route(
        {
          origin: from,
          destination: to,
          travelMode: google.maps.TravelMode.DRIVING,
          drivingOptions: {
            departureTime: new Date(),
            trafficModel: google.maps.TrafficModel.BEST_GUESS,
          },
        },
        (result: any, status: string) => {
          if (status === 'OK' && result) {
            clearFallbackLine();
            directionsRendererRef.current?.setDirections(result);
            setHasRouteLine(true);
            const leg = result.routes[0]?.legs?.[0];
            const eta = leg?.duration_in_traffic?.text || leg?.duration?.text || null;
            const dist = leg?.distance?.text || null;
            setEtaText(eta);
            setDistanceText(dist);
            setSteps(extractRouteSteps(result));
            lastRouteOriginRef.current = { lat: from.lat, lng: from.lng };
            if (leg) {
              onRouteStatsUpdate?.({
                distanceMeters: leg.distance?.value || 0,
                durationMinutes: (leg.duration_in_traffic?.value || leg.duration?.value || 0) / 60,
                distanceLabel: dist || '',
                etaLabel: eta || '',
              });
            }
            return;
          }

          console.warn('[Go7StyleNav] Google Directions failed:', status, '— keeping Mapbox/ORS route');
        }
      );
    },
    [onRouteStatsUpdate]
  );

  useEffect(() => {
    if (!mapReady || !resolvedTarget?.lat) return;
    const from = currentLocation || pickupLocation;
    if (!from?.lat) return;
    fetchRoute(from, resolvedTarget);
  }, [
    mapReady,
    resolvedTarget?.lat,
    resolvedTarget?.lng,
    pickupLocation?.lat,
    pickupLocation?.lng,
    fetchRoute,
  ]);

  useEffect(() => {
    if (!mapReady || !currentLocation?.lat || !resolvedTarget?.lat) return;
    const last = lastRouteOriginRef.current;
    // First GPS: always route from rider. Later: only if moved far from last route start.
    if (!last || distanceMeters(last, currentLocation) > 120) {
      fetchRoute(currentLocation, resolvedTarget);
    }
  }, [mapReady, currentLocation?.lat, currentLocation?.lng, resolvedTarget?.lat, resolvedTarget?.lng, fetchRoute]);

  // HARD FOLLOW — pan map to rider on every GPS update (phones where RAF stalls)
  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !currentLocation?.lat) return;
    try {
      map.panTo({ lat: currentLocation.lat, lng: currentLocation.lng });
      const z = typeof map.getZoom === 'function' ? map.getZoom() || 0 : 0;
      if (z < 15) map.setZoom(17);
    } catch {
      /* ignore */
    }
  }, [mapReady, currentLocation?.lat, currentLocation?.lng]);

  useEffect(() => {
    if (!mapReady || !currentLocation?.lat || !resolvedTarget?.lat) return;
    const interval = window.setInterval(() => {
      if (liveArrivedRef.current) return;
      if (distanceMeters(currentLocation, resolvedTarget) < ARRIVED_RADIUS_M) {
        setArrived(true);
        setSteps([]);
        return;
      }
      if (liveAccuracyRef.current != null && liveAccuracyRef.current > 90) return;
      const last = lastRouteOriginRef.current;
      if (!last) {
        fetchRoute(currentLocation, resolvedTarget);
        return;
      }
      if (distanceMeters(last, currentLocation) > ROUTE_REFRESH_DISTANCE_METERS) {
        fetchRoute(currentLocation, resolvedTarget);
      }
    }, ROUTE_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [mapReady, currentLocation?.lat, currentLocation?.lng, resolvedTarget?.lat, resolvedTarget?.lng, fetchRoute]);

  useEffect(() => {
    if (!currentLocation?.lat || !resolvedTarget?.lat) return;
    const d = distanceMeters(currentLocation, resolvedTarget);
    const isArrived = d < ARRIVED_RADIUS_M;
    setArrived(isArrived);
    if (isArrived && !wasArrivedRef.current) {
      wasArrivedRef.current = true;
      setSteps([]);
      setHasRouteLine(false);
      setEtaText('Arrived');
      setDistanceText('0 m');
      onRouteStatsUpdate?.({
        distanceMeters: 0,
        durationMinutes: 0,
        distanceLabel: '0 m',
        etaLabel: 'Arrived',
      });
      try {
        directionsRendererRef.current?.set('directions', null);
      } catch {
        /* ignore */
      }
      clearFallbackLine();
    } else if (!isArrived) {
      wasArrivedRef.current = false;
    }
  }, [currentLocation?.lat, currentLocation?.lng, resolvedTarget?.lat, resolvedTarget?.lng, onRouteStatsUpdate]);

  useEffect(() => {
    if (arrived || !steps.length || !currentLocation) return;
    setActiveStepIndex(findActiveStepIndex(steps, currentLocation));
  }, [steps, currentLocation?.lat, currentLocation?.lng, arrived]);

  // Distance to turn → camera zoom ref
  useEffect(() => {
    if (arrived || !activeStep?.end || !currentLocation) {
      liveDistToTurnRef.current = null;
      return;
    }
    liveDistToTurnRef.current = distanceMeters(currentLocation, activeStep.end);
  }, [arrived, activeStep, currentLocation?.lat, currentLocation?.lng]);

  useEffect(() => {
    if (arrived || !voiceEnabled || !activeStep || lastSpokenStepRef.current === activeStepIndex) return;
    if (!('speechSynthesis' in window)) return;
    const guide = buildTurnGuidance(activeStep, nextStep, liveDistToTurnRef.current);
    lastSpokenStepRef.current = activeStepIndex;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(
      `${guide.isImminent ? 'Now. ' : ''}${guide.distanceLabel}. ${guide.detail}`
    );
    utterance.rate = 0.95;
    window.speechSynthesis.speak(utterance);
  }, [voiceEnabled, activeStep, activeStepIndex, nextStep, arrived]);

  // Single RAF follow loop — camera + marker + accuracy circle
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    let raf = 0;
    const loop = (ts: number) => {
      try {
        const pos = livePosRef.current;
        if (pos?.lat && pos?.lng) {
          if (!cameraStateRef.current) {
            cameraStateRef.current = createCameraState(pos);
          }
          const dt = lastCameraTsRef.current ? Math.min(64, ts - lastCameraTsRef.current) : 16;
          lastCameraTsRef.current = ts;

          tickFollowCamera(
            map,
            pos,
            liveHeadingRef.current || 0,
            cameraStateRef.current,
            dt,
            {
              speedMps: liveSpeedRef.current,
              distanceToTurnM: liveDistToTurnRef.current,
              arrived: liveArrivedRef.current,
              accuracyM: liveAccuracyRef.current,
              isMoving: liveMovingRef.current && !liveArrivedRef.current,
              lookAheadM: liveArrivedRef.current ? 0 : liveMovingRef.current ? 22 : 8,
            }
          );

          const google = (window as any).google;
          if (google?.maps) {
            const heading = liveHeadingRef.current || 0;
            const moving = liveMovingRef.current;
            let rider = markersRef.current.rider;
            if (!rider) {
              rider = new google.maps.Marker({
                map,
                position: pos,
                icon: go7MotorcycleMarkerIcon(google, heading, 64, moving),
                zIndex: 20,
                title: 'You',
              });
              markersRef.current.rider = rider;
            } else {
              rider.setPosition(pos);
              if (ts - lastIconTsRef.current > MARKER_ICON_MS) {
                lastIconTsRef.current = ts;
                rider.setIcon(go7MotorcycleMarkerIcon(google, heading, 64, moving));
              }
            }

            // Accuracy circle
            const acc = liveAccuracyRef.current;
            const radius =
              acc != null && Number.isFinite(acc)
                ? Math.max(ACCURACY_MIN_M, Math.min(ACCURACY_MAX_M, acc))
                : ACCURACY_MIN_M;

            if (!accuracyCircleRef.current) {
              accuracyCircleRef.current = new google.maps.Circle({
                map,
                center: pos,
                radius,
                fillColor: '#4285F4',
                fillOpacity: 0.12,
                strokeColor: '#4285F4',
                strokeOpacity: 0.35,
                strokeWeight: 1,
                clickable: false,
                zIndex: 1,
              });
            } else {
              accuracyCircleRef.current.setCenter(pos);
              accuracyCircleRef.current.setRadius(radius);
              // Dim when excellent accuracy
              const excellent = acc != null && acc < 15;
              accuracyCircleRef.current.setOptions({
                fillOpacity: excellent ? 0.06 : 0.14,
                strokeOpacity: excellent ? 0.2 : 0.4,
              });
            }
          }
        }
      } catch (e) {
        console.warn('[nav] follow frame error:', e);
      }
      raf = requestAnimationFrame(loop);
      cameraRafRef.current = raf;
    };

    raf = requestAnimationFrame(loop);
    cameraRafRef.current = raf;
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (cameraRafRef.current) cancelAnimationFrame(cameraRafRef.current);
    };
  }, [mapReady]);

  // Pickup / delivery markers
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    const google = (window as any).google;
    if (!map || !google?.maps) return;

    const upsert = (key: string, pos: NavMapLocation | undefined, color: string, title: string) => {
      if (!pos?.lat) {
        markersRef.current[key]?.setMap(null);
        delete markersRef.current[key];
        return;
      }
      if (markersRef.current[key]) {
        markersRef.current[key].setPosition(pos);
        return;
      }
      markersRef.current[key] = new google.maps.Marker({
        map,
        position: pos,
        title,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 11,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 3,
        },
        zIndex: 5,
        label: {
          text: key === 'pickup' ? 'P' : 'D',
          color: '#fff',
          fontSize: '11px',
          fontWeight: '700',
        },
      });
    };

    upsert('pickup', pickupLocation, GO7_ROUTE_COLORS.pickup, 'Pickup');
    upsert('delivery', deliveryLocation, GO7_ROUTE_COLORS.delivery, 'Delivery');
  }, [mapReady, pickupLocation?.lat, pickupLocation?.lng, deliveryLocation?.lat, deliveryLocation?.lng]);

  const distanceToTurn =
    activeStep?.end && currentLocation
      ? distanceMeters(currentLocation, activeStep.end)
      : null;

  let guidance: TurnGuidance | null = null;
  if (!arrived) {
    if (activeStep) {
      guidance = buildTurnGuidance(activeStep, nextStep, distanceToTurn);
    } else if (hasRouteLine && currentLocation?.lat && resolvedTarget?.lat) {
      guidance = buildCompassGuidance(currentLocation, resolvedTarget, distanceText);
    }
  }

  // Fall back to the MapTiler navigation map when Google Maps fails to load.
  // Same follow-camera behaviour so the rider is never left without navigation.
  if (loadError || useBackupMap) {
    return (
      <MapTilerNavMap
        pickupLocation={pickupLocation}
        deliveryLocation={deliveryLocation}
        currentLocation={currentLocation}
        navTarget={navTarget}
        riderHeading={riderHeading}
        isRiderMoving={isMoving}
        onRouteStatsUpdate={onRouteStatsUpdate}
        mode="navigation"
        showRoute
      />
    );
  }

  return (
    <div className="absolute inset-0 w-full h-full min-h-0 bg-gray-900">
      <div ref={containerRef} className="absolute inset-0 w-full h-full" />
      {!mapReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
          <Loader2 className="w-8 h-8 text-[var(--go7-primary,#E0001B)] animate-spin" />
        </div>
      )}

      {mapReady && arrived && (
        <div
          className="absolute left-3 right-3 z-20"
          style={{ top: 'max(4.5rem, calc(env(safe-area-inset-top) + 3.5rem))' }}
        >
          <div className="bg-gray-900 text-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-stretch">
              <div className="flex items-center justify-center bg-[var(--go7-accent,#00B277)] px-4 text-3xl font-bold min-w-[64px]">
                ✓
              </div>
              <div className="flex-1 px-4 py-3.5 min-w-0">
                <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold">
                  You&apos;ve arrived
                </p>
                <p className="text-base font-semibold leading-snug mt-0.5">
                  {destinationLabel || 'Destination'}
                </p>
                <p className="text-xs text-gray-400 mt-1">Use the button below to continue</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {mapReady && !arrived && guidance && (
        <div
          className="absolute left-3 right-3 z-20"
          style={{ top: 'max(4.5rem, calc(env(safe-area-inset-top) + 3.5rem))' }}
        >
          <div
            className={`text-white rounded-2xl shadow-2xl overflow-hidden border ${
              guidance.isImminent
                ? 'bg-[#E0001B] border-[#E0001B]'
                : 'bg-gray-950/95 border-white/10'
            }`}
          >
            <div className="flex items-stretch">
              <div
                className={`flex flex-col items-center justify-center px-3.5 min-w-[76px] py-3 ${
                  guidance.isImminent ? 'bg-black/25' : 'bg-[var(--go7-primary,#E0001B)]'
                }`}
              >
                <span className="text-3xl font-bold leading-none">{guidance.icon}</span>
                {guidance.distanceLabel ? (
                  <span className="text-[12px] font-bold mt-1.5 tabular-nums whitespace-nowrap">
                    {guidance.distanceLabel}
                  </span>
                ) : null}
              </div>
              <div className="flex-1 px-3.5 py-3 min-w-0">
                <p
                  className={`text-[11px] uppercase tracking-wide font-semibold ${
                    guidance.isImminent ? 'text-white' : 'text-red-200/90'
                  }`}
                >
                  {guidance.isImminent
                    ? 'Now'
                    : guidance.distanceLabel
                      ? `In ${guidance.distanceLabel}`
                      : 'Next'}
                </p>
                <p className="text-lg font-bold leading-snug mt-0.5">{guidance.action}</p>
                {guidance.street ? (
                  <p className="text-sm text-white/90 leading-snug mt-0.5 truncate">
                    {guidance.street}
                  </p>
                ) : (
                  <p className="text-sm text-white/75 leading-snug mt-0.5 line-clamp-2">
                    {guidance.detail}
                  </p>
                )}
                {guidance.thenLabel ? (
                  <p className="text-[11px] text-white/55 mt-1.5 truncate">{guidance.thenLabel}</p>
                ) : hasRouteLine ? (
                  <p className="text-[11px] text-white/45 mt-1.5">Follow the red route on the map</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setVoiceEnabled((v) => !v)}
                className="px-3 flex items-center justify-center text-white/80 hover:text-white border-l border-white/15"
                aria-label={voiceEnabled ? 'Mute voice' : 'Enable voice'}
              >
                {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {mapReady && !arrived && (etaText || distanceText) && (
        <div
          className="absolute left-3 right-3 z-20 pointer-events-none"
          style={{
            top: guidance
              ? 'max(10.75rem, calc(env(safe-area-inset-top) + 9.75rem))'
              : 'max(4.5rem, calc(env(safe-area-inset-top) + 3.5rem))',
          }}
        >
          <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">ETA</p>
              <p className="text-base font-bold text-gray-900 truncate">
                {etaText || 'Calculating…'}
              </p>
            </div>
            {distanceText && (
              <div className="text-right shrink-0">
                <p className="text-[10px] text-gray-500">Remaining</p>
                <p className="text-sm font-semibold text-gray-800">{distanceText}</p>
              </div>
            )}
            {destinationLabel ? (
              <div className="text-right min-w-0 max-w-[40%]">
                <p className="text-[10px] text-gray-500">To</p>
                <p className="text-xs font-semibold text-gray-700 truncate">{destinationLabel}</p>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
